/* ZSportsTech — section-clinic-book.js
   Z-Bat Clinic: SLOT-FIRST booking.

     pick net + date + time  →  hold  →  Shopify checkout  →  paid
                                                                ↓
                        confirmPaidHolds() (every minute, server-side) creates
                        the calendar event on the OWNER's calendar and emails the
                        customer. The customer is not invited to the event — they
                        get our branded confirmation instead, nothing from Google.

   The hold blocks the slot for everyone the moment it is created, and lapses
   after HOLD_TTL_MIN if the customer never pays. Nothing is written to the
   owner's Google Calendar until the payment lands — that is the whole point.

   `hold` is the only write an anonymous visitor can make. It carries a
   Liquid-signed page token (proves the request came from this theme today)
   plus two invisible bot-repellents: a honeypot field a real shopper never
   fills, and the time between opening the modal and pressing submit (a bot
   posts in milliseconds; nobody types a name, email and phone that fast).
   Cloudflare Turnstile used to sit in front of this too — removed 2026-09-28,
   it was flaky in the field (client error 300010) and cost real bookings from
   Meta ads. If the page token is unavailable we do NOT fall back to an
   unprotected call — the CTA reverts to the plain pay-first product link
   instead.
   ─────────────────────────────────────────────────── */
(function () {
  'use strict';

  var S = window.SCNS;
  if (!S) return;

  var triggers = document.querySelectorAll('[data-ns-clinic-book]');
  if (!triggers.length) return;

  var cfg = S.cfg || {};
  var NETS = [{ value: 'Net 1', label: 'Net 1' }, { value: 'Net 2', label: 'Net 2' }];
  var CLINIC_FEE_INR  = 199;    /* keep in step with variant CL-1's price      */
  var CLINIC_LIST_INR = 1200;   /* ...and with its compare-at price            */

  /* Both figures are read from the two constants above rather than typed into
     the markup: the fee already feeds the Meta pixel, so a price change that
     updated one and not the other would quietly report the wrong value. */
  function inr(n) { return '₹' + n.toLocaleString('en-IN'); }

  var OFFER_SAVE_PCT = Math.round((1 - CLINIC_FEE_INR / CLINIC_LIST_INR) * 100);

  var OFFER_HTML =
    '<div class="sc-nsm__offer-head">' +
      '<span class="sc-nsm__offer-tag">Special offer</span>' +
      '<span class="sc-nsm__offer-save">Save ' + OFFER_SAVE_PCT + '%</span>' +
    '</div>' +
    '<div class="sc-nsm__offer-prices">' +
      '<span class="sc-nsm__offer-was">' + inr(CLINIC_LIST_INR) + '</span>' +
      '<span class="sc-nsm__offer-now">' + inr(CLINIC_FEE_INR) + '</span>' +
    '</div>';

  /* ── Meta pixel — clinic booking funnel ───────────────────────────────
     Dataset 1380826504029254 ("zbats"). fbq is already on the page: the
     Facebook & Instagram sales channel loads fbevents.js in the MAIN frame
     (lax web-pixel sandbox), so we call it directly. DO NOT add a second
     base-pixel snippet anywhere — it would double-count every event on the
     site, Purchase included. See docs/meta-clinic-tracking.md.

     Money is always INR. The storefront converts the DISPLAYED price outside
     India while checkout still charges INR, so reporting the display currency
     would report amounts nobody was ever charged.

     The retry exists only for the fast clicker who beats fbevents.js off the
     network; it gives up quietly after ~5s rather than throwing. -------- */
  function zbatFb(fn, tries) {
    if (typeof window.fbq === 'function') {
      try { fn(window.fbq); } catch (e) {}
      return;
    }
    tries = tries || 0;
    if (tries > 20) return;
    setTimeout(function () { zbatFb(fn, tries + 1); }, 250);
  }

  function fbEventId(prefix) {
    return prefix + '-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
  }

  /* Funnel entry. Bound BELOW on every trigger and BEFORE the readiness guard,
     so the click is reported whether the slot picker opens or the script
     stands down to the plain pay-first product link — otherwise a stood-down
     page would report nothing at all and Meta's automatic event logging would
     go back to guessing `SubscribedButtonClick` at it.

     Once per page load: the three CTAs (hero, §05 band, §05 card) are the same
     intent, and a visitor who scrolls back up and clicks again has not started
     a second booking. */
  function trackBookOpened() {
    if (window.__zbatClinicOpened) return;
    window.__zbatClinicOpened = true;
    zbatFb(function (fbq) {
      fbq('trackCustom', 'ClinicBookOpened', {
        content_name: 'Z-Bat Clinic session',
        value: CLINIC_FEE_INR,
        currency: 'INR'
      }, { eventID: fbEventId('clinic-open') });
    });
  }

  /* A slot chosen is real intent — it is the step where the long tail drops
     out, and ClinicBookOpened → ClinicSlotPicked → Schedule is the only way to
     see whether we are losing people at the calendar or at the form. Once per
     page load; re-picking a time is not a new decision. */
  function trackSlotPicked(state) {
    if (window.__zbatClinicSlotPicked) return;
    window.__zbatClinicSlotPicked = true;
    zbatFb(function (fbq) {
      fbq('trackCustom', 'ClinicSlotPicked', {
        content_name: 'Z-Bat Clinic session',
        clinic_net: state.net,
        clinic_date: state.date,
        value: CLINIC_FEE_INR,
        currency: 'INR'
      }, { eventID: fbEventId('clinic-slot') });
    });
  }

  /* `Schedule` — a standard event, fired the moment the hold lands: the slot
     is locked for everyone else and we hold a name, an email and a phone
     number. This is the clinic's optimisation event.

     Deliberately NOT `Lead`: the AI Bat Fit quiz already reports Lead on this
     same dataset, and mixing a paid clinic appointment in with quiz enquiries would
     blur what either campaign is optimising towards.

     Deliberately NOT AddToCart or InitiateCheckout either — the /cart/add.js
     call and the /checkout redirect that follow this are Shopify cart events,
     so the F&I channel already reports both. Firing our own would double them. */
  function trackScheduled(fields, cal) {
    /* One per contact per page load, so a retry after a failed checkout
       cannot re-fire it. */
    window.__zbatClinicScheduled = window.__zbatClinicScheduled || {};
    var key = String(fields.email || fields.phone).toLowerCase();
    if (window.__zbatClinicScheduled[key]) return;
    window.__zbatClinicScheduled[key] = true;

    var email = String(fields.email || '').trim().toLowerCase();
    var name  = String(fields.name || '').trim().toLowerCase().split(/\s+/);

    /* This field is a plain "10-digit mobile" — unlike the quiz's dial-code
       picker, nothing has normalised it. Meta wants digits with the country
       code and no "+", so add 91 only to a bare 10-digit Indian number and
       leave anything that already carries a code alone. */
    var phone = String(fields.phone || '').replace(/\D/g, '');
    if (phone.length === 10) phone = '91' + phone;

    var country = '';
    try {
      country = String((window.Shopify && window.Shopify.country) || '').toLowerCase();
    } catch (e) {}

    /* eventID is free now and saves a migration later: sending the same id
       from the Conversions API is how Meta collapses the browser and server
       copies into one Schedule instead of two. */
    var eventID = fbEventId('clinic-sched');

    zbatFb(function (fbq) {
      /* Advanced matching. PLAIN values on purpose — fbq SHA-256 hashes them
         in the browser before anything is transmitted. Re-calling init on an
         already-initialised pixel updates its user data; it does not create a
         second pixel and does not fire a PageView. */
      try {
        fbq('init', '1380826504029254', {
          em: email,
          ph: phone,
          fn: name[0] || '',
          ln: name.slice(1).join(' '),
          country: country || 'in'
        });
      } catch (e) {}

      fbq('track', 'Schedule', {
        content_name:     'Z-Bat Clinic session',
        content_category: 'clinic',
        value:            CLINIC_FEE_INR,
        currency:         'INR',
        clinic_net:       cal.state.net,
        clinic_date:      cal.state.date,
        clinic_start:     cal.state.start,
        lead_source:      'z_bat_clinic'
      }, { eventID: eventID });
    });
  }

  /* Report the click on every trigger, in the capture phase, before the guard
     below can return: the ready path calls preventDefault() on the same click
     and the stood-down path navigates away, and we want the event either way. */
  triggers.forEach(function (el) {
    el.addEventListener('click', function () { trackBookOpened(); }, true);
  });

  /* Every precondition must hold, or we stand down and let section-nspkg.js do
     the old pay-first add-to-cart. We must NOT half-bind: both scripts listen
     for [data-ns-variant], so a click would open this popup AND add to cart. */
  var ready = S.enabled && cfg.clinicHoldToken && cfg.clinicVariantId;
  if (!ready) return;

  triggers.forEach(function (el) {
    /* Claim the element. section-nspkg.js runs after us (script order) and
       selects on [data-ns-variant], so removing it hands us sole ownership.
       The variant id we need comes from cfg.clinicVariantId, not the attribute. */
    el.removeAttribute('data-ns-variant');
    el.addEventListener('click', function (e) {
      e.preventDefault();
      openClinicBooking();
    });
  });

  /* ── Razorpay, loaded on demand ───────────────────────────────────────────
     Only fetched once a shopper has actually held a slot: the clinic page is a
     marketing page first, and most visitors never book. */
  /* We do not know the gateway until the hold comes back, but the modal opens
     long before that. Preload only when Razorpay is configured for this store,
     which the storefront can infer from the clinic config. */
  function clinicPaymentLikely() { return !!cfg.clinicVariantId; }

  /* NEVER reuse a window.Razorpay that is already on the page. The Razorpay
     Magic Checkout app embed loads magic-checkout.js on every storefront page,
     and ITS Razorpay is built to hand off to Shopify's checkout: opened for the
     clinic it shows the payment options, then ~10s later covers them with
     "Redirecting you to checkout · Do not close this tab" and never recovers
     (reproduced 2026-09-22, with and without an order). The UPI QR behind it
     keeps counting down, so shoppers saw the payment "time out".

     So we load the standard checkout.js ourselves, keep ITS constructor, and
     put Magic's back on window so the cart's Magic Checkout is untouched. */
  var rzpPromise = null;
  var StdRazorpay = null;
  function loadRazorpay() {
    if (StdRazorpay) return Promise.resolve(StdRazorpay);
    if (rzpPromise) return rzpPromise;
    rzpPromise = new Promise(function (resolve, reject) {
      var prior = window.Razorpay;
      var s = document.createElement('script');
      s.src = 'https://checkout.razorpay.com/v1/checkout.js';
      s.async = true;
      s.onload = function () {
        StdRazorpay = window.Razorpay;
        if (prior && prior !== StdRazorpay) window.Razorpay = prior;
        if (StdRazorpay === prior) console.warn('[clinic] checkout.js did not replace the Magic Razorpay build');
        resolve(StdRazorpay);
      };
      s.onerror = function () { rzpPromise = null; reject(new Error('razorpay_unreachable')); };
      document.head.appendChild(s);
    });
    return rzpPromise;
  }

  /* Razorpay Standard Checkout: name, email, phone and a payment method. No
     address — there is nothing to ship and nowhere to bill, the shopper is
     walking into Parel. All three fields are prefilled from what they just
     typed, so in practice the next thing they see is their UPI app.

     There is no cart in this path at all, which is the other bug it fixes: a
     bat abandoned in the cart days ago used to ride into the clinic checkout
     and could not be removed from there. */
  function payWithRazorpay(hold, msg, busy, m) {
    return loadRazorpay()
      .then(function () {
        busy(true, 'Opening payment…');
        /* The hold response now carries the Razorpay order, so the common path
           makes no second round trip at all. The fallback stays because the
           server deliberately returns the hold even if creating that order
           failed — the slot is theirs either way. */
        if (hold.payment && hold.payment.razorpayOrderId) return hold.payment;
        return S.call({ action: 'clinicPayment', holdId: hold.holdId, pageToken: cfg.clinicHoldToken });
      })
      .then(function (pay) {
        return new Promise(function (resolve, reject) {
          /* Razorpay does not always call us back. Pressing Escape inside its
             sheet can leave it showing "Redirecting you to checkout" forever
             without ever firing ondismiss — the shopper is then frozen on
             "Opening payment…" with no message and no way forward. Reproduced
             here, and it is what Naman hit.

             So every exit goes through settle(): whoever gets there first wins,
             and a watchdog settles it if Razorpay's DOM disappears without
             telling us. Never leave someone staring at a dead button. */
          /* Get our own sheet out of the way while Razorpay is up.

             Razorpay's container carries z-index 2147483647 against our 200, so
             on desktop it covers us correctly — verified. On phones it does not
             always win: Razorpay switches rendering mode there (the "Redirecting
             you to checkout" path), and our overlay carries a backdrop-filter,
             which creates its own containing block. Naman's client had to close
             the booking popup to reach the payment sheet; on Naman's phone it
             never became visible at all.

             Rather than fight that per-browser, we simply stop being on screen.
             Two modals were never the intent — you are paying, not still
             picking a slot — so this is also the better shape. The panel keeps
             its DOM and its state, so the confirmation (or the error) renders
             into the same body when we come back. */
          var overlay = document.querySelector('.sc-nsm');
          function hideBooking(on) {
            if (overlay) overlay.classList.toggle('sc-nsm--paying', !!on);
          }

          /* An exit that does not depend on Razorpay behaving.

             Razorpay's sheet can wedge on "Redirecting you to checkout" and
             never fire ondismiss — reproduced, twice. While our own sheet was
             merely underneath, the shopper could still close it. Now that we
             hide it, a wedge would leave them with NO interface at all and a
             reload as the only way out, which also loses the hold. That is a
             worse failure than the one hiding it solves, so it needs its own
             way back.

             Held until 20s so a normal payment is never cluttered by it —
             by then a working sheet has long since rendered. */
          var escapeBtn = null, escapeTimer = null;
          function armEscape() {
            escapeTimer = setTimeout(function () {
              if (settled) return;
              escapeBtn = document.createElement('button');
              escapeBtn.type = 'button';
              escapeBtn.className = 'sc-nsm__payesc';
              escapeBtn.textContent = 'Having trouble? Close payment';
              escapeBtn.addEventListener('click', function () {
                settle(reject, new Error('payment_dismissed'));
              });
              /* Appended AFTER Razorpay's container, so at equal z-index the
                 later element paints on top. */
              document.body.appendChild(escapeBtn);
            }, 20000);
          }
          function disarmEscape() {
            if (escapeTimer) clearTimeout(escapeTimer);
            if (escapeBtn && escapeBtn.parentNode) escapeBtn.parentNode.removeChild(escapeBtn);
            escapeBtn = null;
          }

          var settled = false, seen = false, watch = null;
          function settle(fn, v) {
            if (settled) return;
            settled = true;
            if (watch) clearInterval(watch);
            disarmEscape();
            try { if (rzp && rzp.close) rzp.close(); } catch (e) {}
            hideBooking(false);
            fn(v);
          }

          var rzp = new StdRazorpay({
            key: pay.keyId,
            order_id: pay.razorpayOrderId,
            amount: pay.amount,
            currency: pay.currency,
            name: 'Z-Bat Clinic',
            description: '30-minute assessment · ' + hold.date + ' · ' + hold.start + ' · ' + hold.net,
            image: '//zsportstech.com/cdn/shop/files/logo.png',
            prefill: pay.prefill,
            notes: { hold_id: hold.holdId },
            theme: { color: '#D4AF37' },
            /* Razorpay hands back the signature in the RESPONSE BODY, which is
               what makes it verifiable server-side — a webhook's signature
               lives in a header, and Apps Script cannot read headers. */
            handler: function (r) {
              busy(true, 'Confirming your slot…');
              S.call({
                action: 'clinicPaymentConfirm',
                holdId: hold.holdId,
                pageToken: cfg.clinicHoldToken,
                razorpayOrderId:   r.razorpay_order_id,
                razorpayPaymentId: r.razorpay_payment_id,
                razorpaySignature: r.razorpay_signature
              }).then(function (v) { settle(resolve, v); }, function (e) {
                /* Past this line the money HAS moved. Whatever the backend
                   said, collapse it to one signal so no error code — including
                   one added later — can reach a shopper who has paid and read
                   as "that failed, try again". The real code goes to the
                   console for us, and the sweeper recovers the booking. */
                console.error('[clinic] post-payment confirm failed:', e && e.message,
                              '| payment', r.razorpay_payment_id);
                settle(reject, new Error('payment_pending_confirm'));
              });
            },
            modal: {
              /* escape:false — Escape is what triggers the stuck state above,
                 and there is a working close button two inches away. */
              escape: false,
              confirm_close: true,
              /* Dismissing is not an error. The hold stands until its TTL, so
                 they can pay again without picking the slot twice. */
              ondismiss: function () { settle(reject, new Error('payment_dismissed')); }
            }
          });
          rzp.on('payment.failed', function () { settle(reject, new Error('payment_failed')); });
          hideBooking(true);
          armEscape();
          rzp.open();

          /* The watchdog. Once Razorpay's container has appeared, its
             disappearance means the sheet is gone — whether or not we were
             told. Without this the only exit from the stuck state is a reload,
             which loses the hold. */
          watch = setInterval(function () {
            if (settled) return;
            var here = !!document.querySelector('.razorpay-container');
            if (here) { seen = true; return; }
            if (seen) settle(reject, new Error('payment_dismissed'));
          }, 1000);
        });
      })
      .then(function (done) {
        /* Purchase has to be fired here. The Shopify order is created through
           the Admin API rather than by a shopper walking through checkout, so
           Shopify's own pixel never sees it and would report nothing. */
        trackPurchase(hold, done);
        showConfirmation(m, hold, done);
      });
  }

  /* ── WHERE THE CLINIC IS ──────────────────────────────────────────────────
     One place, used for both the printed address and the map. The theme only
     ever said "Parel, Mumbai" and the Shopify fulfilment location is in
     Bhuleshwar — a different part of the city — so nothing in the codebase was
     safe to infer a street address from. Correct this and the map follows. */
  var CLINIC_PLACE = {
    name:  'Z-Bat Clinic',
    /* Taken from the store's own verified Google Business listing (4.8★, 203
       reviews) rather than invented — the theme only ever said "Parel, Mumbai"
       and the Shopify fulfilment location is in Bhuleshwar, a different part of
       the city, so neither was safe to print on a confirmation. */
    lines: ['Valencia Towers, basement, Raj Kamal Marg',
            'Parel East, Mumbai, Maharashtra 400012'],
    /* Searched by name + locality rather than by the raw address string: the
       listing resolves exactly, and a street string geocodes badly in Mumbai. */
    mapQuery: 'Z-Bat Clinic, Parel, Mumbai'
  };

  function mapEmbedUrl() {
    /* The keyless embed. The Maps Embed API needs a billing-enabled key and
       this does not, which matters for a page that must not break quietly on
       a day we are paying for traffic. */
    return 'https://maps.google.com/maps?q=' + encodeURIComponent(CLINIC_PLACE.mapQuery) +
           '&z=15&output=embed';
  }
  function mapLinkUrl() {
    return 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(CLINIC_PLACE.mapQuery);
  }

  /* ── The confirmation ─────────────────────────────────────────────────────
     Deliberately not S.done()'s tick-and-a-sentence. This is the last thing a
     shopper sees after paying and the only artefact they carry to the nets, so
     it has to answer "what did I buy, when, where, and how do I prove it".
     The booking id is the bit staff can look up on the phone. */
  function showConfirmation(m, hold, done) {
    var b = (done && done.booking) || {};
    var when = prettyWhen({ date: b.date || hold.date, start: b.start || hold.start });
    var net  = b.net || hold.net;
    var ref  = b.bookingId || '';
    var ord  = (done && done.orderName) || '';

    var rows = [
      ['When',    when],
      ['Net',     net],
      ['Duration','30 minutes'],
      ['Paid',    '₹' + CLINIC_FEE_INR]
    ];
    if (b.name)  rows.push(['Name',  b.name]);
    if (b.phone) rows.push(['Phone', b.phone]);

    var el = document.createElement('div');
    el.className = 'sc-clconf';
    el.innerHTML =
      '<div class="sc-clconf__tick" aria-hidden="true">✓</div>' +
      '<h3 class="sc-clconf__title">Your slot is booked</h3>' +
      '<p class="sc-clconf__sub">A confirmation is on its way to your email.</p>' +

      /* Same substance as snippets/email-spam-note.liquid, which says it before
         booking; this says it after, when they are actually waiting for the
         mail. Kept deliberately: the ask to mark it "not spam" is the one thing
         a recipient can do that helps the NEXT email land, and naming My
         Sessions is what makes a missed email a non-event rather than a lost
         booking. Both go when sending moves off gmail.com — at that point this
         reads as a lack of confidence in our own email. */
      '<p class="sc-clconf__mail">' +
        'If it is not in your inbox within a minute, check your <strong>spam</strong> ' +
        'or promotions folder — marking it “not spam” helps the next one land. ' +
        'Your booking is on My Sessions either way.' +
      '</p>' +

      (ref ? '<div class="sc-clconf__ref">' +
               '<span class="sc-clconf__reflabel">Booking ID</span>' +
               '<span class="sc-clconf__refval"></span>' +
             '</div>' : '') +

      '<p class="sc-clconf__shot">📸 Please screenshot this screen and bring it with you.</p>' +

      '<dl class="sc-clconf__rows"></dl>' +

      '<div class="sc-clconf__where">' +
        '<span class="sc-clconf__wherelabel">Where</span>' +
        '<p class="sc-clconf__addr"></p>' +
        '<div class="sc-clconf__map">' +
          '<iframe title="Map to the Z-Bat Clinic" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>' +
        '</div>' +
        '<a class="sc-clconf__dir" target="_blank" rel="noopener">Open in Google Maps →</a>' +
      '</div>' +

      '<p class="sc-clconf__note">Arrive 5 minutes early. Bring your own bat if you have ' +
      'one — we will measure against it.</p>' +
      (ord ? '<p class="sc-clconf__order"></p>' : '') +
      '<a class="sc-nsm__done-cta" href="/pages/my-sessions">View my sessions →</a>';

    /* textContent, not innerHTML, for every value that came back from the
       server or was typed by the shopper. */
    if (ref) el.querySelector('.sc-clconf__refval').textContent = ref;
    if (ord) el.querySelector('.sc-clconf__order').textContent = 'Order ' + ord;

    var dl = el.querySelector('.sc-clconf__rows');
    rows.forEach(function (r) {
      var dt = document.createElement('dt'); dt.textContent = r[0];
      var dd = document.createElement('dd'); dd.textContent = r[1];
      dl.appendChild(dt); dl.appendChild(dd);
    });

    var addr = el.querySelector('.sc-clconf__addr');
    addr.textContent = CLINIC_PLACE.name;
    CLINIC_PLACE.lines.forEach(function (line) {
      addr.appendChild(document.createElement('br'));
      addr.appendChild(document.createTextNode(line));
    });
    el.querySelector('.sc-clconf__map iframe').src = mapEmbedUrl();
    el.querySelector('.sc-clconf__dir').href = mapLinkUrl();

    m.body.innerHTML = '';
    m.body.appendChild(el);
  }

  /* "2026-09-13" + "14:30" → "Sat 13 Sept, 2:30 PM". Built here rather than
     pulled from the calendar module: by this point the modal has been replaced
     by the done screen and the calendar is gone. */
  function prettyWhen(hold) {
    try {
      var d = new Date(hold.date + 'T' + hold.start + ':00+05:30');
      var day = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' });
      var time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
      return day + ', ' + time;
    } catch (e) { return hold.date + ' ' + hold.start; }
  }

  /* `Purchase` — standard event, the campaign's conversion. Deduplicated by
     eventID against nothing today (there is no server-side CAPI send yet), but
     the id is emitted anyway so adding one later does not double-count. */
  function trackPurchase(hold, done) {
    zbatFb(function (fbq) {
      fbq('track', 'Purchase', {
        content_name: 'Z-Bat Clinic session',
        content_type: 'product',
        clinic_net: hold.net,
        clinic_date: hold.date,
        value: CLINIC_FEE_INR,
        currency: 'INR'
      }, { eventID: 'clinic-purchase-' + String((done && done.orderName) || hold.holdId) });
    });
  }

  /* ── The popup ───────────────────────────────────────────────────────── */
  function openClinicBooking() {
    var booking = false;

    /* Warm the payment SDK while they pick a slot. It is ~100 KB off a third
       party; fetching it at submit put the download inside the wait everyone
       was complaining about. Failure is ignored — loadRazorpay() will simply
       try again later. */
    if (clinicPaymentLikely()) { try { loadRazorpay().catch(function () {}); } catch (e) {} }

    S.modal({
      title: 'Book a clinic session',
      subtitle: '30 minutes · one net session · pay to confirm',
      wide: true,
      render: function (body, m) {
        /* fillMs travels to the backend so it can reject a submit that arrives
           faster than a human could plausibly have typed a name, email and
           phone — see CFG.MIN_FILL_MS in Code.gs. */
        var openedAt = Date.now();

        var msg = S.banner();
        body.appendChild(msg.node);

        var offer = S.el('div', 'sc-nsm__offer');
        offer.innerHTML = OFFER_HTML;
        body.appendChild(offer);

        /* Say so on screen when the rehearsal switch is armed. Without this the
           only way to tell which gateway you are about to get is to reach the
           payment sheet and look — which is how a test silently ran down the
           wrong path once already. */

        var cal = null, submit = null;

        var calMount = S.el('div');
        body.appendChild(calMount);

        var nameI  = S.input('text', 'Your full name', '', 'name');
        var emailI = S.input('email', 'you@example.com', '', 'email');
        var phoneI = S.input('tel', '10-digit mobile', '', 'tel');
        body.appendChild(S.field('Name', nameI));
        var row = S.el('div', 'sc-nsm__row2');
        row.appendChild(S.field('Email', emailI));
        row.appendChild(S.field('Phone', phoneI));
        body.appendChild(row);

        /* Honeypot. A real shopper never sees or fills this — it sits off
           the visible canvas rather than display:none, because some bots
           skip display:none fields on purpose. Any value here means a bot;
           the backend rejects silently rather than naming the reason. */
        /* Named so no autofill profile matches it: a browser that filled
           a "website" field would silently lock a real shopper out. */
        var hpI = S.input('text', '', '', 'off');
        hpI.name = 'sc_nsm_hp';
        hpI.tabIndex = -1;
        hpI.setAttribute('aria-hidden', 'true');
        hpI.style.position = 'absolute';
        hpI.style.left = '-9999px';
        hpI.style.width = '1px';
        hpI.style.height = '1px';
        hpI.style.opacity = '0';
        body.appendChild(hpI);

        submit = S.el('button', 'sc-nsm__submit', 'Continue to payment');
        submit.type = 'button';
        submit.disabled = true;
        body.appendChild(submit);

        body.appendChild(S.el('p', 'sc-nsm__note', 'Your slot is held for a few minutes while you pay'));

        cal = S.calendar({
          mount: calMount,
          nets: NETS,
          initialNet: 'Net 1',
          /* The clinic is paid at checkout, so an off-hours slot cannot be
             requested — rejecting one would owe a refund. Show them, disabled. */
          autoOnly: true,
          loadSlots: function (state) {
            return S.call({ action: 'slots', date: state.date, duration: 30, net: state.net });
          },
          onSlot: function (start) {
            if (submit) submit.disabled = !start;
            if (start) { msg.clear(); trackSlotPicked(cal.state); }
          },
          onError: function (err) { msg.error(S.errText(err)); }
        });

        function busy(on, label) {
          submit.disabled = on;
          submit.textContent = on ? (label || 'Working…') : 'Continue to payment';
        }

        submit.addEventListener('click', function () {
          if (booking) return;
          msg.clear();

          if (!cal.state.start)      { msg.error(S.message('missing_start')); return; }
          if (!nameI.value.trim())   { msg.error(S.message('missing_name')); nameI.focus(); return; }
          if (!S.isEmail(emailI.value))    { msg.error(S.message('bad_email')); emailI.focus(); return; }
          if (!phoneI.value.trim())  { msg.error(S.message('missing_phone')); phoneI.focus(); return; }
          if (!S.isPhone(phoneI.value)) { msg.error(S.message('bad_phone')); phoneI.focus(); return; }

          booking = true;
          busy(true, 'Holding your slot…');

          S.call({
              action: 'hold',
              date: cal.state.date,
              start: cal.state.start,
              net: cal.state.net,
              name: nameI.value.trim(),
              email: emailI.value.trim().toLowerCase(),
              phone: S.normPhone(phoneI.value),
              pageToken: cfg.clinicHoldToken,
              hp: hpI.value,
              fillMs: Date.now() - openedAt
            })
            .then(function (res) {
              busy(true, 'Opening checkout…');

              /* Meta: the slot is held and we have their details. Fired here
                 rather than on the checkout redirect — the hold is the point
                 of no return for the calendar, and a visitor who abandons the
                 payment page has still taken a slot off the board. */
              trackScheduled({
                name: nameI.value,
                email: emailI.value,
                phone: phoneI.value
              }, cal);

              /* Which way the money goes is decided server-side and travels
                 back on the hold, so CLINIC_PAYMENT_MODE is the single switch
                 and this file needs no deploy to follow it. */
              if (res.paymentMode === 'razorpay') return payWithRazorpay(res, msg, busy, m);

              /* Shopify checkout — the original path, still the fallback.
                 The hold id rides on the LINE ITEM, not the cart. Cart-level
                 attributes are unreliable in the Orders API; line-item
                 properties are not. The `_` prefix hides it at checkout. */
              return fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ items: [{
                  id: Number(cfg.clinicVariantId),
                  quantity: 1,
                  properties: { _ns_hold: res.holdId }
                }] })
              }).then(function (r) {
                if (!r.ok) return r.text().then(function (t) { throw new Error(t.slice(0, 200)); });
                window.location.assign('/checkout');
              });
            })
            .catch(function (err) {
              booking = false;
              busy(false);
              var code = err && err.message;
              if (code === 'slot_taken' || code === 'slot_not_found') {
                msg.error(S.message('slot_taken'));
                cal.refresh();
              } else if (code === 'hold_limit_reached' || code === 'phone_hold_limit' ||
                         code === 'too_fast' ||
                         code === 'busy_try_again' || code === 'holds_disabled') {
                msg.error(S.errText(err));
              } else if (code === 'payment_dismissed') {
                /* They closed the payment sheet. The hold stands until its TTL,
                   so this is a pause, not a failure — say so, and leave the
                   slot and the form exactly as they were. */
                msg.info('Payment cancelled. Your slot is still held for a few minutes — press Continue when you are ready.');
              } else if (code === 'payment_failed') {
                msg.error('That payment did not go through. Nothing has been charged — you can try again.');
              } else if (code === 'hold_expired') {
                msg.error('Your slot was released while the payment was open. Please pick a time again.');
                cal.refresh();
              } else if (code === 'payment_pending_confirm') {
                /* EVERY way the confirm can fail AFTER Razorpay took the money
                   lands here. The shopper has paid; the one thing we must never
                   do is imply it failed and invite a second payment. The sweeper
                   will pick this up within five minutes on its own. */
                msg.info('Payment received — we are still confirming your slot. ' +
                         'You will get an email shortly. Please do NOT pay again. ' +
                         'If nothing arrives in 10 minutes, call us on +91 93249 66322.');
              } else if (code === 'razorpay_unreachable' || code === 'payments_unconfigured' ||
                         code === 'payment_start_failed') {
                msg.error('We could not open the payment window. Please try again, or call us on +91 93249 66322.');
              } else if (code === 'login_required') {
                msg.error('Please sign in to book a clinic session.');
              } else if (code === 'page_expired' || code === 'unauthorized') {
                /* unauthorized here is the honeypot's generic refusal, not a real
                   auth failure — 'Please sign in again' would be actively wrong
                   for a shopper who never signed in. Same reload-the-page fix as
                   page_expired, so it gets the same wording. */
                msg.error(S.message('page_expired'));
              } else {
                console.error('[clinic] hold failed:', err);
                msg.error(S.errText(err));
              }
            });
        });
      }
    });
  }
}());
